import clsx from "clsx";
import { type ReactElement, type ReactNode } from "react";
import { DropIndicator, GridList, type GridListProps, useDragAndDrop } from "react-aria-components";

import style from "./ReorderableList.module.css";

export interface DraggableItemData {
  id: string;
  name: string;
}

export type DraggableItem<T = unknown> = DraggableItemData & T;

interface ReorderableListProps<T> extends Omit<GridListProps<DraggableItem<T>>, "children"> {
  items: DraggableItem<T>[];
  onReorder: (items: DraggableItem<T>[]) => void;
  renderDragPreview?: (item: DraggableItem<T>) => ReactElement;
  children: (item: DraggableItem<T>) => ReactNode;
  dropIndicatorClassName?: string;
}

export function ReorderableList<T extends DraggableItemData>({
  items,
  onReorder,
  renderDragPreview,
  className,
  children,
  "aria-label": ariaLabel = "Reorderable list",
  ...props
}: ReorderableListProps<T>) {
  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys) => {
      return [...keys].map((key) => {
        const item = items.find((item) => item.id === key);
        return {
          "text/plain": item?.name ?? item?.id ?? "",
          "item-data": JSON.stringify(item),
        };
      });
    },
    onReorder: onReorder
      ? (e) => {
          const sourceKey = String(e.keys.values().next().value);
          const targetKey = String(e.target.key);

          // Get current positions
          const sourceIndex = items.findIndex((item) => item.id === sourceKey);
          const targetIndex = items.findIndex((item) => item.id === targetKey);

          // Calculate new position, accounting for self-referential drags
          let newPosition;

          if (sourceIndex === targetIndex) {
            // If dragging onto itself, don't change position
            newPosition = sourceIndex;
          } else if (e.target.dropPosition === "before") {
            // When dropping before target
            newPosition = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
          } else {
            // "after"
            // When dropping after target
            newPosition = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
          }

          // Apply the new order to the item list
          const tempItems = [...items];
          const draggedItem = tempItems.splice(sourceIndex, 1);
          tempItems.splice(newPosition, 0, draggedItem[0]);

          // Only update if position actually changed
          if (newPosition !== sourceIndex) {
            onReorder?.(tempItems);
          }
        }
      : undefined,
    getDropOperation(target, types, allowedOperations) {
      // For reordering, we want to return 'move' operation
      if (types.has("text/plain") && target) {
        return "move";
      }

      // Default fallback
      return allowedOperations[0] || "cancel";
    },
    renderDragPreview: (items) => {
      // If custom preview renderer is provided
      if (renderDragPreview) {
        // Extract the item data
        let itemData = null;
        try {
          if (items[0]["item-data"]) {
            itemData = JSON.parse(items[0]["item-data"]);
          }
        } catch (e) {
          console.error("Failed to parse item data:", e);
          return <div>Error in preview</div>;
        }

        if (!itemData) {
          return <div>Loading...</div>;
        }

        return renderDragPreview(itemData);
      }

      // Default fallback preview if no custom renderer provided
      return <div>{items[0]["text/plain"]}</div>;
    },
    renderDropIndicator(target) {
      return (
        <DropIndicator
          target={target}
          className={clsx(props.dropIndicatorClassName, {
            [style.dropIndicator]: (isDropTarget: boolean) => isDropTarget,
          })}
        />
      );
    },
  });

  return (
    <GridList
      items={items}
      dragAndDropHooks={dragAndDropHooks}
      className={className}
      aria-label={ariaLabel}
      style={props.style}
    >
      {(item) => children(item)}
    </GridList>
  );
}
